import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import productService from "./productService";

const errMsg = (error) =>
  error.response?.data?.message || error.message || "Something went wrong";

export const fetchProducts = createAsyncThunk(
  "product/fetchAll",
  async (params, thunkAPI) => {
    try {
      const res = await productService.getAll(params);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: errMsg(error) });
    }
  }
);

export const createProduct = createAsyncThunk(
  "product/create",
  async (formData, thunkAPI) => {
    try {
      const res = await productService.create(formData);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: errMsg(error) });
    }
  }
);

export const updateProduct = createAsyncThunk(
  "product/update",
  async (formData, thunkAPI) => {
    try {
      const res = await productService.update(formData);
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: errMsg(error) });
    }
  }
);

export const deleteProduct = createAsyncThunk(
  "product/delete",
  async (id, thunkAPI) => {
    try {
      await productService.remove(id);
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: errMsg(error) });
    }
  }
);

const productSlice = createSlice({
  name: "product",
  initialState: {
    list: [],
    isLoading: false,   // list load mate
    isSaving: false,    // create/update/delete mate
    isError: false,
    message: "",
    errors: {},         // field wise validation errors
  },
  reducers: {
    clearProductError: (state) => {
      state.isError = false;
      state.message = "";
      state.errors = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchProducts.pending, (s) => { s.isLoading = true; })
      .addCase(fetchProducts.fulfilled, (s, a) => { s.isLoading = false; s.list = a.payload; })
      .addCase(fetchProducts.rejected, (s, a) => {
        s.isLoading = false; s.isError = true;
        s.message = a.payload?.message || "Failed to load products";
      })
      // create
      .addCase(createProduct.pending, (s) => { s.isSaving = true; s.isError = false; s.errors = {}; })
      .addCase(createProduct.fulfilled, (s, a) => { s.isSaving = false; s.list.unshift(a.payload); })
      .addCase(createProduct.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Create failed";
        s.errors = a.payload?.errors || {};
      })
      // update
      .addCase(updateProduct.pending, (s) => { s.isSaving = true; s.isError = false; s.errors = {}; })
      .addCase(updateProduct.fulfilled, (s, a) => {
        s.isSaving = false;
        s.list = s.list.map((p) => (p.id === a.payload.id ? a.payload : p));
      })
      .addCase(updateProduct.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Update failed";
        s.errors = a.payload?.errors || {};
      })
      // delete
      .addCase(deleteProduct.pending, (s) => { s.isSaving = true; })
      .addCase(deleteProduct.fulfilled, (s, a) => {
        s.isSaving = false;
        s.list = s.list.filter((p) => p.id !== a.payload);
      })
      .addCase(deleteProduct.rejected, (s, a) => {
        s.isSaving = false; s.isError = true;
        s.message = a.payload?.message || "Delete failed";
      });
  },
});

export const { clearProductError } = productSlice.actions;
export default productSlice.reducer;